import { createContext, useContext, useReducer, useEffect, useRef } from 'react'
import { authService } from '@services/enhancedApiService'
import { setSentryUser, clearSentryUser } from '@config/sentry'
import { authAttempts } from '@utils/authAttemptState'
import toast from 'react-hot-toast'

// Auth Context
const AuthContext = createContext()

// Auth Actions
const AUTH_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGOUT: 'LOGOUT',
  SET_USER: 'SET_USER',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR'
}

// Initial state
const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null
}

// Auth reducer
function authReducer(state, action) {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      }
    
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null
      }
    
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      }
    
    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        isLoading: false
      }
    
    case AUTH_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false
      }
    
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      }
    
    default:
      return state
  }
}

/**
 * Auth Provider Component
 */
export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState)
  const pendingLoginAttempt = useRef(null)

  // Check authentication status on mount
  // For landing page: Skip initial auth check since all pages are public
  // Auth will be handled during login/signup actions
  useEffect(() => {
    if (authAttempts.getStatus().blocked) {
      localStorage.removeItem('user')
      dispatch({ type: AUTH_ACTIONS.SET_USER, payload: null })
      clearSentryUser()
      return () => authAttempts.invalidate('provider-unmount')
    }

    // Skip API call, just check localStorage for any cached user data
    const cachedUser = localStorage.getItem('user')
    if (cachedUser) {
      try {
        const user = JSON.parse(cachedUser)
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: user })
        // Set Sentry user context
        setSentryUser(user)
      } catch (e) {
        localStorage.removeItem('user')
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: null })
        clearSentryUser()
      }
    } else {
      dispatch({ type: AUTH_ACTIONS.SET_USER, payload: null })
      clearSentryUser()
    }
    return () => authAttempts.invalidate('provider-unmount')
  }, [])

  useEffect(() => {
    const onAuthenticationFailure = () => {
      localStorage.removeItem('user')
      localStorage.removeItem('mockToken')
      pendingLoginAttempt.current = null
      dispatch({ type: AUTH_ACTIONS.LOGOUT })
      clearSentryUser()
    }
    window.addEventListener('auth:logout', onAuthenticationFailure)
    return () => window.removeEventListener('auth:logout', onAuthenticationFailure)
  }, [])

  /**
   * Check if user is authenticated
   * For landing page - all pages are public, so auth check is optional
   */
  const checkAuthStatus = async () => {
    const attempt = authAttempts.capture()
    const authStatus = authAttempts.getStatus()
    if (authStatus.blocked && !authStatus.logoutPending) return null
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true })
      
      // Try to get current user, but fail silently since all pages are public
      const snapshot = authService.getCurrentUserSnapshot
        ? await authService.getCurrentUserSnapshot(attempt, {
          skipAuthRefresh: authStatus.logoutPending
        })
        : { user: await authService.getCurrentUser(attempt) }
      if (!authAttempts.isCurrent(attempt)) return null
      const user = snapshot?.user
      
      if (user) {
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: user })
        // Set Sentry user context
        setSentryUser(user)
      } else {
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: null })
        clearSentryUser()
      }
    } catch (error) {
      if (!authAttempts.isCurrent(attempt)) return null
      if (error.response?.status === 401) {
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: null })
        clearSentryUser()
      } else {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false })
      }
    }
  }

  useEffect(() => authAttempts.subscribe(change => {
    if (change.kind !== 'peer-change') return
    if (change.blocked) {
      localStorage.removeItem('user')
      pendingLoginAttempt.current = null
      dispatch({ type: AUTH_ACTIONS.LOGOUT })
      clearSentryUser()
      return
    }
    checkAuthStatus()
  }), [])

  /**
   * Login user
   */
  const login = async (credentials) => {
    const attempt = authAttempts.beginAuthentication()
    pendingLoginAttempt.current = attempt
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true })
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR })

      const response = await authService.login(credentials, attempt)
      if (!authAttempts.isCurrent(attempt)) return { success: false, stale: true }

      if (response.success) {
        // Check if 2FA is required
        if (response.requiresTwoFactor) {
          dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false })
          return {
            success: true,
            requiresTwoFactor: true,
            tempToken: response.tempToken
          }
        }

        const acceptedAttempt = authAttempts.acceptAuthentication(attempt, response.authSession)
        if (response.user) localStorage.setItem('user', JSON.stringify(response.user))
        pendingLoginAttempt.current = null

        dispatch({ type: AUTH_ACTIONS.LOGIN_SUCCESS, payload: response.user })

        // Set Sentry user context
        setSentryUser(response.user)

        toast.success(response.sessionReplaced
          ? 'Your oldest active session was signed out to make room for this login.'
          : 'Login successful! Redirecting to dashboard...')

        // Redirect to dashboard - cookies will be automatically sent
        const dashboardUrl = import.meta.env.VITE_DASHBOARD_URL || 'http://localhost:3001'
        setTimeout(() => {
          if (authAttempts.isCurrent(acceptedAttempt)) window.location.href = dashboardUrl
        }, 500)

        return { success: true }
      } else {
        throw new Error(response.message || 'Login failed')
      }
    } catch (error) {
      if (!authAttempts.isCurrent(attempt)) return { success: false, stale: true }
      const errorMessage = error.response?.data?.error?.message ||
                          error.response?.data?.message ||
                          error.message ||
                          'Login failed'
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMessage })
      toast.error(errorMessage)
      return { success: false, error: errorMessage }
    }
  }

  /**
   * Verify 2FA code during login
   */
  const verifyLogin2FA = async (tempToken, code, isBackupCode = false) => {
    const attempt = pendingLoginAttempt.current
    if (!attempt || !authAttempts.isCurrent(attempt)) return { success: false, stale: true }
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true })
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR })

      const response = await authService.verifyLogin2FA(tempToken, code, isBackupCode, attempt)
      if (!authAttempts.isCurrent(attempt)) return { success: false, stale: true }

      if (response.success) {
        const acceptedAttempt = authAttempts.acceptAuthentication(attempt, response.authSession)
        if (response.user) localStorage.setItem('user', JSON.stringify(response.user))
        pendingLoginAttempt.current = null
        dispatch({ type: AUTH_ACTIONS.LOGIN_SUCCESS, payload: response.user })
        setSentryUser(response.user)
        toast.success(response.sessionReplaced
          ? 'Your oldest active session was signed out to make room for this login.'
          : 'Login successful! Redirecting to dashboard...')

        const dashboardUrl = import.meta.env.VITE_DASHBOARD_URL || 'http://localhost:3001'
        setTimeout(() => {
          if (authAttempts.isCurrent(acceptedAttempt)) window.location.href = dashboardUrl
        }, 500)

        return { success: true }
      } else {
        throw new Error(response.message || '2FA verification failed')
      }
    } catch (error) {
      if (!authAttempts.isCurrent(attempt)) return { success: false, stale: true }
      const errorMessage = error.response?.data?.error?.message ||
                          error.response?.data?.message ||
                          error.message ||
                          '2FA verification failed'
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMessage })
      toast.error(errorMessage)
      return { success: false, error: errorMessage }
    }
  }

  /**
   * Register user
   */
  const signup = async (userData) => {
    const attempt = authAttempts.beginAuthentication()
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true })
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR })
      
      const response = await authService.signup(userData, attempt)
      if (!authAttempts.isCurrent(attempt)) return { success: false, stale: true }
      
      if (response.success) {
        const acceptedAttempt = authAttempts.acceptAuthentication(attempt, response.authSession)
        if (response.user) localStorage.setItem('user', JSON.stringify(response.user))
        const enrollment = response.enrollment
        const message = enrollment?.status === 'pending'
          ? 'Account created. Paper account setup is pending. Sign in to retry.'
          : enrollment?.status === 'blocked'
            ? 'Account created. Paper account setup needs support review.'
            : 'Account created successfully! Redirecting to login...'
        toast.success(message)
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false })
        
        // Redirect to login page after successful signup
        setTimeout(() => {
          if (authAttempts.isCurrent(acceptedAttempt)) window.location.href = '/login'
        }, 1500)
        
        return { success: true, enrollment }
      } else {
        throw new Error(response.message || 'Signup failed')
      }
    } catch (error) {
      if (!authAttempts.isCurrent(attempt)) return { success: false, stale: true }
      // Extract detailed error message from nested error object
      const errorMessage = error.response?.data?.error?.message || 
                          error.response?.data?.message || 
                          error.message || 
                          'Signup failed'
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMessage })
      toast.error(errorMessage)
      return { success: false, error: errorMessage }
    }
  }

  /**
   * Logout user
   */
  const logout = async () => {
    const attempt = authAttempts.beginLogout()
    pendingLoginAttempt.current = null
    localStorage.removeItem('user')
    localStorage.removeItem('mockToken')
    dispatch({ type: AUTH_ACTIONS.LOGOUT })
    clearSentryUser()
    try {
      const result = await authService.logout(attempt)
      if (!authAttempts.finishLogout(attempt, result.serverRevoked)) {
        return { localSignedOut: true, serverRevoked: false, stale: true }
      }
      if (result.serverRevoked) toast.success('Logged out successfully')
      else toast.error('Signed out here, but server sign-out could not be confirmed.')
      return { localSignedOut: true, serverRevoked: result.serverRevoked }
    } catch {
      if (!authAttempts.finishLogout(attempt, false)) {
        return { localSignedOut: true, serverRevoked: false, stale: true }
      }
      return { localSignedOut: true, serverRevoked: false }
    }
  }

  /**
   * Clear error state
   */
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR })
  }

  /**
   * Refresh user data
   */
  const refreshUser = async () => {
    const attempt = authAttempts.capture()
    try {
      const snapshot = authService.getCurrentUserSnapshot
        ? await authService.getCurrentUserSnapshot(attempt)
        : { user: await authService.getCurrentUser(attempt) }
      if (!authAttempts.isCurrent(attempt)) return null
      const user = snapshot?.user
      dispatch({ type: AUTH_ACTIONS.SET_USER, payload: user })
      if (user) setSentryUser(user)
      return user
    } catch (error) {
      if (authAttempts.isCurrent(attempt) && error.response?.status === 401) {
        dispatch({ type: AUTH_ACTIONS.LOGOUT })
        clearSentryUser()
      }
      return null
    }
  }

  const value = {
    // State
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    error: state.error,
    
    // Actions
    login,
    verifyLogin2FA,
    signup,
    logout,
    clearError,
    refreshUser,
    checkAuthStatus
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

/**
 * Custom hook to use auth context
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext)
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  
  return context
}

/**
 * HOC for protecting routes that require authentication
 */
// eslint-disable-next-line react-refresh/only-export-components
export function withAuth(Component) {
  return function AuthenticatedComponent(props) {
    const { isAuthenticated, isLoading } = useAuth()
    
    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="spinner w-8 h-8"></div>
        </div>
      )
    }
    
    if (!isAuthenticated) {
      window.location.href = '/'
      return null
    }
    
    return <Component {...props} />
  }
}

export default AuthContext
